/**
 * CoutProductionPrint — impression PDF de la section « Coût de production »
 * (module Rentabilité). Deux écrans (décision Olivier 2026-07-14), chacun démarre
 * une page ; une table longue déborde naturellement sur la page suivante :
 *   P1 — Usage du temps        (CapacityBillableTime)
 *   P2 — Coût de production     (CapacityAnalysisPage, tables ; « Coût par personne »
 *        peut couvrir 2 pages)
 * L'onglet « Temps de production » (saisie) est exclu du PDF.
 *
 * Gabarit d'impression FIABLE (identique aux rapports Finance) : overlay portalé
 * dans <body> + pagination native CSS Paged Media (PeoplePrint.css). Le gabarit
 * précédent (tdabc-print, hauteur fixe + overflow:hidden + visibility) coupait le
 * contenu et sortait des pages blanches.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useP } from '../../i18n/useP';
import CapacityBillableTime from './CapacityBillableTime';
import CapacityAnalysisPage from './CapacityAnalysisPage';
import '../hr/print/PeoplePrint.css';
import '../hr/PeoplePrintDocument.css';
import '../financials/financialsScreenPrint.css';

function ReportHeader({ category, title, tenant }) {
  const p = useP();
  const dateStr = new Date().toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return (
    <header className="ppl-print__report-header">
      <div>
        <div className="ppl-print__report-eyebrow">{category}</div>
        <h2 className="ppl-print__report-title">{title}</h2>
      </div>
      <div className="ppl-print__report-meta">
        <div><strong>{tenant?.name}</strong></div>
        <div>{p('Édité le ')}{dateStr}</div>
      </div>
    </header>
  );
}

export default function CoutProductionPrint({ activeTenant, year, unit = 'kchf', onClose }) {
  const { t } = useTranslation();
  const p = useP();
  const [ready, setReady] = useState(false);
  const tenantId = activeTenant?.id;
  const noop = () => {};

  useEffect(() => {
    document.body.classList.add('people-printing');
    return () => document.body.classList.remove('people-printing');
  }, []);

  useEffect(() => {
    const prev = document.title;
    const label = [activeTenant?.name, year].filter(Boolean).join(' — ');
    document.title = `${p('Coût de production')}${label ? ` — ${label}` : ''}`;
    return () => { document.title = prev; };
  }, [activeTenant?.name, year, p]);

  useEffect(() => {
    const handler = () => onClose();
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => window.print();

  return createPortal(
    <div className="ppl-print-overlay">
      <div className="ppl-print-controls">
        <div className="ppl-print-controls__left">
          <span className="ppl-print-controls__title">
            {p('Aperçu — Coût de production')} · {activeTenant?.name}
          </span>
          {!ready && <span className="ppl-print-controls__loading">{p('⟳ Chargement des données…')}</span>}
        </div>
        <div className="ppl-print-controls__right">
          <button
            className="ppl-print-btn ppl-print-btn--print"
            onClick={handlePrint}
            disabled={!ready}
            title={ready ? p('Imprimer ou enregistrer en PDF') : p('Patientez…')}
          >
            {ready ? p('⎙ Imprimer / PDF') : p('⟳ Chargement…')}
          </button>
          <button className="ppl-print-btn ppl-print-btn--close" onClick={onClose}>{p('✕ Fermer')}</button>
        </div>
      </div>

      <div className="ppl-print__preview">
        <div className="ppl-print">
          <section className="ppl-print__section ppl-print__section--wide fin-screenprint">
            <ReportHeader category={t('phrases:p_1995f795ba')} title={p('Usage du temps')} tenant={activeTenant} />
            <CapacityBillableTime tenantId={tenantId} year={year} />
          </section>
          <section className="ppl-print__section ppl-print__section--wide fin-screenprint">
            <ReportHeader category={t('phrases:p_1995f795ba')} title={t('phrases:p_6edef097ac')} tenant={activeTenant} />
            <CapacityAnalysisPage embedded tablesOnly moneyUnit={unit} activeTenant={activeTenant} addToast={noop} year={year} />
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
